import { useCallback, useMemo } from 'react';
import { CustomTextField, DropDownField, RadioButtonField } from '@/components/shared/CustomField';
import { TemplateField, TemplateFieldTypeEnum, TextFormatEnum } from '@/__generated__/graphql';
import {
  ProposedPaintArea,
  ProposedPaintDiameter,
  ProposedPaintLength,
} from '@/components/shared/CalculatedFields';
import { InputAdormantHandlerMap } from '@/types';
import { useUnitConversion } from '@/hooks/useUnitConversion';

type Props = {
  templateField: TemplateField;
  isEditing: boolean;
  value?: string;
  onChange?: (value: string) => void;
  inputAdormantHandlerMap: InputAdormantHandlerMap;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
};

export const TemplateFieldRenderer = ({
  templateField,
  isEditing,
  value,
  onChange,
  inputAdormantHandlerMap,
  disabled,
  error,
  helperText,
}: Props) => {
  const { getUnitSuffix } = useUnitConversion();

  const handleFieldValueChange = useCallback(
    (data: string) => {
      if (onChange) onChange(data);
    },
    [onChange]
  );

  const props = useMemo(() => {
    const unitSuffix = getUnitSuffix({
      unit: templateField.unit,
      field: templateField.key || undefined,
    });
    const suffixString = unitSuffix ? ` (${unitSuffix})` : '';
    const title = `${templateField.name}${suffixString}`;

    if (isEditing) {
      return {
        mode: 'Edit' as const,
        id: templateField.id,
        title,
        options: templateField.options,
        value,
        onFieldValueChange: handleFieldValueChange,
        disabled,
        required: templateField.isRequired,
        valueFormat: templateField?.displayConfig?.textFormat ?? TextFormatEnum.TitleCase,
        disableClearable: disabled,
        error,
        helperText,
      };
    }
    return {
      mode: 'ReadOnly' as const,
      id: templateField.id,
      title,
      value,
    };
  }, [
    disabled,
    error,
    handleFieldValueChange,
    helperText,
    isEditing,
    templateField?.displayConfig?.textFormat,
    templateField.id,
    templateField.name,
    templateField.options,
    templateField.isRequired,
    templateField.unit,
    templateField.key,
    value,
    getUnitSuffix,
  ]);

  switch (templateField.type) {
    case TemplateFieldTypeEnum.Textarea:
    case TemplateFieldTypeEnum.Input:
      return <CustomTextField sx={{ mt: 2 }} {...props} />;

    case TemplateFieldTypeEnum.Numeric:
      return <CustomTextField sx={{ mt: 2 }} {...props} numeric />;
    case TemplateFieldTypeEnum.SingleSelect:
      return <DropDownField sx={{ mt: 2 }} {...props} />;
    case TemplateFieldTypeEnum.RadioButton:
      return <RadioButtonField sx={{ mt: 2 }} {...props} />;
    case TemplateFieldTypeEnum.MeasurementPaintLength:
      return (
        <ProposedPaintLength
          {...props}
          displayConfig={templateField.displayConfig}
          inputAdormantHandlerMap={inputAdormantHandlerMap}
          templateFieldId={templateField.id}
        />
      );
    case TemplateFieldTypeEnum.MeasurementPaintDiameter:
      return (
        <ProposedPaintDiameter
          {...props}
          displayConfig={templateField.displayConfig}
          onFieldValueChange={onChange}
          disableField={true}
        />
      );

    case TemplateFieldTypeEnum.MeasurementPaintArea:
      return (
        <ProposedPaintArea
          {...props}
          displayConfig={templateField.displayConfig}
          onFieldValueChange={onChange}
        />
      );
    default:
      return <></>;
  }
};
